/**
 * Redesigned Ticket List Page - Modern UX
 *
 * Features:
 * - Visual stats dashboard
 * - Tab-based filtering
 * - Enhanced ticket cards
 * - Quick actions
 * - Better mobile support
 * - Improved visual hierarchy
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTickets } from '@/services/tickets';
import type { Ticket, TicketFilters, TicketStatus, TicketCategory, TicketPriority } from '@/types';

// Stats Card Component
function StatsCard({ icon, label, value, color, onClick, active }: any) {
  return (
    <button
      onClick={onClick}
      className={`relative overflow-hidden rounded-xl border-2 p-6 transition-all hover:shadow-lg ${
        active
          ? `${color} border-current shadow-md scale-105`
          : 'bg-white border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${active ? 'bg-white/20' : 'bg-gray-100'}`}>
          {icon}
        </div>
        <div className="text-left">
          <p className={`text-sm font-medium ${active ? 'text-white' : 'text-gray-600'}`}>
            {label}
          </p>
          <p className={`text-3xl font-bold ${active ? 'text-white' : 'text-gray-900'}`}>
            {value}
          </p>
        </div>
      </div>
    </button>
  );
}

// Enhanced Ticket Card Component
function TicketCard({ ticket, t }: { ticket: Ticket; t: any }) {
  const statusConfig = {
    open: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
    in_progress: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500' },
    waiting_user: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
    resolved: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
    closed: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', dot: 'bg-gray-500' },
  };

  const priorityConfig = {
    low: { icon: '↓', color: 'text-gray-500', bg: 'bg-gray-100' },
    medium: { icon: '→', color: 'text-blue-600', bg: 'bg-blue-100' },
    high: { icon: '↑', color: 'text-orange-600', bg: 'bg-orange-100' },
    urgent: { icon: '⚡', color: 'text-red-600', bg: 'bg-red-100' },
  };

  const status = statusConfig[ticket.status as keyof typeof statusConfig];
  const priority = priorityConfig[ticket.priority as keyof typeof priorityConfig];

  const getTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Link
      to={`/tickets/${ticket.id}`}
      className={`group relative block rounded-xl border-2 ${status.border} ${status.bg} p-6 transition-all hover:shadow-xl hover:-translate-y-1`}
    >
      {/* Priority Badge - Top Right */}
      <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-bold ${priority.bg} ${priority.color} flex items-center gap-1`}>
        <span className="text-lg">{priority.icon}</span>
        {t(`priority.${ticket.priority}`)}
      </div>

      {/* Unread Badge */}
      {ticket.unread_messages_count !== undefined && ticket.unread_messages_count > 0 && (
        <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg animate-pulse">
          {ticket.unread_messages_count} new
        </div>
      )}

      {/* Header */}
      <div className="mb-4 pr-24">
        <div className="flex items-center gap-2 mb-2">
          <span className={`w-2 h-2 rounded-full ${status.dot} animate-pulse`}></span>
          <span className="text-xs font-mono font-bold text-gray-500">
            {ticket.ticket_number}
          </span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-2">
          {ticket.subject}
        </h3>
      </div>

      {/* Meta Information */}
      <div className="flex flex-wrap gap-3 mb-4">
        {/* Status */}
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.text} border ${status.border}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
          {t(`status.${ticket.status}`)}
        </span>

        {/* Category */}
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          {t(`category.${ticket.category}`)}
        </span>

        {/* Messages Count */}
        {ticket.messages_count !== undefined && ticket.messages_count > 0 && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-300">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            {ticket.messages_count} replies
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-gray-500 border-t border-gray-200 pt-4">
        <div className="flex items-center gap-2">
          {ticket.assigned_admin && (
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">
                {ticket.assignedAdmin.name.charAt(0).toUpperCase()}
              </div>
              <span className="font-medium text-gray-700">{ticket.assignedAdmin.name}</span>
            </div>
          )}
        </div>

        {ticket.last_reply_at && (
          <span className="flex items-center gap-1 text-xs">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {getTimeAgo(ticket.last_reply_at)}
          </span>
        )}
      </div>

      {/* Hover Arrow */}
      <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
        <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  );
}

export function TicketListPage() {
  const { t } = useTranslation('tickets');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TicketStatus | 'all'>('all');

  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 15,
  });

  const [filters, setFilters] = useState<TicketFilters>({
    status: undefined,
    category: undefined,
    priority: undefined,
    search: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    per_page: 15,
    page: 1,
  });

  // Calculate stats
  const stats = {
    all: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    waiting_user: tickets.filter(t => t.status === 'waiting_user').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
  };

  useEffect(() => {
    loadTickets();
  }, [filters]);

  const loadTickets = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await getTickets(filters);

      setTickets(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || t('notifications.errorLoading'));
      console.error('Error loading tickets:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (status: TicketStatus | 'all') => {
    setActiveTab(status);
    setFilters(prev => ({
      ...prev,
      status: status === 'all' ? undefined : status,
      page: 1,
    }));
  };

  const handleFilterChange = (field: keyof TicketFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              {t('title')}
            </h1>
            <p className="text-lg text-gray-600">
              {t('subtitle')}
            </p>
          </div>
          <Link
            to="/tickets/new"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-primary-700 hover:to-primary-800 transition-all transform hover:-translate-y-0.5"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('createTicket')}
          </Link>
        </div>

        {/* Telegram Note */}
        <div className="flex items-center gap-3 px-5 py-3.5 bg-sky-50 border border-sky-200 rounded-xl text-sm text-sky-800">
          <svg className="w-5 h-5 flex-shrink-0 text-sky-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L8.32 13.617l-2.96-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.828.942z"/>
          </svg>
          <span>
            {t('telegramNote')}{' '}
            <a href="https://t.me/AxetraIO" target="_blank" rel="noopener noreferrer" className="font-semibold underline hover:text-sky-600">
              @AxetraIO
            </a>
          </span>
        </div>

        {/* Stats Dashboard */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatsCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
            label="All Tickets"
            value={pagination.total}
            color="bg-gradient-to-br from-gray-600 to-gray-700"
            onClick={() => handleTabChange('all')}
            active={activeTab === 'all'}
          />
          <StatsCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
            label="Open"
            value={stats.open}
            color="bg-gradient-to-br from-blue-500 to-blue-600"
            onClick={() => handleTabChange('open')}
            active={activeTab === 'open'}
          />
          <StatsCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            }
            label="In Progress"
            value={stats.in_progress}
            color="bg-gradient-to-br from-yellow-500 to-orange-500"
            onClick={() => handleTabChange('in_progress')}
            active={activeTab === 'in_progress'}
          />
          <StatsCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            label="Waiting"
            value={stats.waiting_user}
            color="bg-gradient-to-br from-purple-500 to-pink-500"
            onClick={() => handleTabChange('waiting_user')}
            active={activeTab === 'waiting_user'}
          />
          <StatsCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            label="Resolved"
            value={stats.resolved}
            color="bg-gradient-to-br from-green-500 to-emerald-600"
            onClick={() => handleTabChange('resolved')}
            active={activeTab === 'resolved'}
          />
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder={t('list.searchPlaceholder')}
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <select
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium"
                value={filters.category || ''}
                onChange={(e) => handleFilterChange('category', e.target.value as TicketCategory || undefined)}
              >
                <option value="">{t('category.all')}</option>
                <option value="technical">{t('category.technical')}</option>
                <option value="billing">{t('category.billing')}</option>
                <option value="feature_request">{t('category.feature_request')}</option>
                <option value="bug_report">{t('category.bug_report')}</option>
                <option value="account">{t('category.account')}</option>
                <option value="other">{t('category.other')}</option>
              </select>
            </div>
          </div>

          {/* Active Filters Indicator */}
          {(filters.search || filters.category) && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              {filters.search && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                  Search: "{filters.search}"
                  <button onClick={() => handleFilterChange('search', '')} className="hover:bg-indigo-200 rounded-full p-0.5">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              )}
              {filters.category && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                  Category: {t(`category.${filters.category}`)}
                  <button onClick={() => handleFilterChange('category', undefined)} className="hover:bg-indigo-200 rounded-full p-0.5">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 flex items-start gap-3">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-red-800 font-semibold">Error loading tickets</h3>
              <p className="text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Ticket List */}
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : tickets.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center">
              <svg className="w-12 h-12 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('list.noTickets')}</h3>
            <p className="text-gray-600 mb-6">
              {t('empty.description')}
            </p>
            <Link to="/tickets/new" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('createTicket')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {tickets.map((ticket) => (
              <TicketCard key={ticket.id} ticket={ticket} t={t} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && pagination.lastPage > 1 && (
          <div className="flex items-center justify-between bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('pagination.previous')}
            </button>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Page {pagination.currentPage} of {pagination.lastPage}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {pagination.total} total tickets
              </p>
            </div>

            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.lastPage}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {t('pagination.next')}
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
